import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Brain, Bone, Activity, Baby, Smile, Eye, Stethoscope, Search, ChevronRight } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import { useTranslation } from '../hooks/useTranslation';
import { Skeleton } from '../components/ui/skeleton';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping
const iconMap = {
  Heart: Heart,
  Brain: Brain,
  Bone: Bone,
  Activity: Activity,
  Baby: Baby,
  Smile: Smile,
  Eye: Eye,
  Stethoscope: Stethoscope
};

export default function Treatments() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/public/treatments`);
      if (response.ok) {
        const data = await response.json();
        setTreatments(data.treatments || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const getTreatmentSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  // Filter treatments
  const filteredTreatments = treatments.filter(treatment => {
    const matchesSearch = searchTerm === '' || 
      treatment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (treatment.procedures || []).some(p => p.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === '' || treatment.name === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Loading skeleton
  const TreatmentSkeleton = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <Skeleton className="h-32 w-full" />
      <div className="p-6">
        <Skeleton className="h-6 w-32 mb-2" />
        <Skeleton className="h-4 w-full mb-4" />
        <div className="flex flex-wrap gap-2 mb-4">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="h-4 w-28" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(193_95%_95%)] to-[hsl(210_40%_98%)]">
      <PublicHeader currentPage="treatments" />
      
      {/* Hero Section */}
      <section className="pt-28 pb-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[hsl(var(--foreground))] mb-4" data-testid="treatments-title">
              {t('treatments.heroTitle')} <span className="text-[hsl(var(--primary))]">{t('treatments.heroTitleHighlight')}</span>
            </h1>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              {t('treatments.heroSubtitle')}
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search treatments or procedures..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent shadow-sm"
                data-testid="search-treatments"
              />
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === '' 
                  ? 'bg-[hsl(var(--primary))] text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              All Categories
            </button>
            {treatments.map(treatment => (
              <button
                key={treatment._id || treatment.name}
                onClick={() => setSelectedCategory(treatment.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === treatment.name 
                    ? 'bg-[hsl(var(--primary))] text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {treatment.name}
              </button>
            ))}
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredTreatments.length}</span> treatment categories
            </p>
          </div>

          {/* Treatments Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => <TreatmentSkeleton key={i} />)}
            </div>
          ) : filteredTreatments.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No treatments found</h3>
              <p className="text-gray-500">Try adjusting your search</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTreatments.map((treatment, index) => {
                const IconComponent = iconMap[treatment.icon] || Stethoscope;
                
                return (
                  <div
                    key={treatment._id || index}
                    onClick={() => navigate(`/treatments/${getTreatmentSlug(treatment.name)}`)}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                    data-testid={`treatment-card-${index}`}
                  >
                    {/* Header with gradient */}
                    <div className={`h-32 bg-gradient-to-r ${treatment.color || 'from-gray-500 to-gray-600'} relative overflow-hidden`}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <IconComponent className="w-16 h-16 text-white/80" />
                      </div>
                      <div className="absolute inset-0 bg-black/10"></div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-[hsl(var(--primary))] transition-colors">
                        {treatment.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                        {treatment.description}
                      </p>
                      
                      {/* Procedures */}
                      <div className="mt-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Procedures</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(treatment.procedures || []).slice(0, 3).map((procedure, idx) => (
                            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                              {procedure}
                            </span>
                          ))}
                          {(treatment.procedures || []).length > 3 && (
                            <span className="px-2 py-1 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-xs rounded-full">
                              +{treatment.procedures.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Cost Range */}
                      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Starting from</p>
                          <p className="text-lg font-bold text-[hsl(var(--primary))]">{treatment.costRange}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center group-hover:bg-[hsl(var(--primary))] transition-colors">
                          <ChevronRight className="w-5 h-5 text-[hsl(var(--primary))] group-hover:text-white transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(193_80%_50%)] rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Need help finding the right treatment?
            </h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Our medical experts can guide you to the best treatment options based on your condition and preferences.
            </p>
            <button 
              onClick={() => navigate('/register')}
              className="px-8 py-3 bg-white text-[hsl(var(--primary))] rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Get Free Consultation
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
